import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { loginSchema } from "../../utils/error_schema";
import { loginRequest } from "../../services";
import { FloatingField, PasswordField, SubmitButton } from "../../components/Common";
import { useFormValidation } from "../../utils/custom_hook";
import { storeAuthTokens, extractAuthData } from "../../utils/authHelpers";
import { queryString, resolveApiFormErrors } from "../../utils";
import "./auth.css";

const INITIAL_FORM_VALUES = { email: "", password: "" };
const INITIAL_FORM_ERRORS = { email: "", password: "" };

export function Login() {
  const navigate = useNavigate();
  const [resError, setResError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const {
    inputValue,
    inputError,
    setInputError,
    handleInputChange,
    validateForm,
  } = useFormValidation(
    INITIAL_FORM_VALUES,
    INITIAL_FORM_ERRORS,
    loginSchema
  );

  const submit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();

    if (!isValid) return;

    setIsLoading(true);
    setResError("");
    setInputError(INITIAL_FORM_ERRORS);

    try {
      const res = await loginRequest(inputValue);

      // Handle successful authentication
      const authData = extractAuthData(res);
      if (res?.success && authData) {
        storeAuthTokens(authData.access, authData.refresh, authData.user);
        navigate("/home");
        return;
      }

      // Handle verification needed
      if (res?.results?.needs_verification) {
        navigate(
          "/verification/" + res?.results?.user?.id + "?" + queryString(res?.results?.user)
        );
        return;
      }
      const { fieldErrors, displayError } = resolveApiFormErrors(
        res,
        INITIAL_FORM_ERRORS,
        { fallbackMessage: "Something went wrong!" }
      );
      setInputError(fieldErrors);
      setResError(displayError);
    } catch (error) {
      setResError("Something went wrong! Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title="Login">
      <form onSubmit={submit}>
        <div className="text-2xl mb-8">Welcome to Fincobox</div>

        <div className="mb-4">
          <FloatingField
            controlId="floatingInput"
            label="Email"
            labelClass="_inInput_fx"
            type="email"
            placeholder="Email"
            id="email"
            name="email"
            data-testid="fincobox-login-email"
            onChange={({ target: { name, value } }) => handleInputChange(name, value)}
            focus={!!inputError.email}
            error={inputError.email}
            value={inputValue.email}
          />
        </div>

        <div className="mb-4">
          <PasswordField
            controlId="floatingInput"
            label="Password"
            labelClass="_inInput_fx"
            id="password1"
            placeholder="Password"
            name="password"
            data-testid="fincobox-login-password"
            maxLength={50}
            onChange={({ target: { name, value } }) => handleInputChange(name, value)}
            focus={!!inputError.password}
            error={inputError.password}
            value={inputValue.password}
          />
        </div>

        <div className="check_forget_align">
          <p className="mt-3">
            <Link to="/forgot-password" data-testid="fincobox-login-forgot-password">Forgot Password?</Link>
          </p>
        </div>

        <div className="mt-4">
          {resError && <p className="invalid-feedback">{resError}</p>}
          <SubmitButton text="Log in" isLoading={isLoading} id="refUrl" data-testid="fincobox-login-submit" />
        </div>

        <p className="mt-3 text-center">
          Don't have an account? <Link to="/signup">Sign up now</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
